import { CommonModule } from "@angular/common";
import { NgModule, APP_INITIALIZER } from "@angular/core";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";

import { AuthGuardService } from "./services/auth-guard.service";
import { AuthService } from "./services/auth.service";
import { DialogService } from "./services/dialog.service";
import { AppLoadService } from "./services/app-load.service";
import { SharedDataService } from "./services/shared-data.service";
import { NavigationService } from "./services/navigation.service";
import { DataService } from "./services/dataservices/data.service";
import { NotificationService } from "./services/notification.service";
import { ShortcutService } from "./services/shortcut.service";
import { ExtensionService } from "./services/extension.service";
import { ScrollService } from "./services/scroll.service";
import { EventService } from "./services/event.service";
import { ListService } from "./services/list.service";
import { ImportShortcutApiService } from "./services/import-shortcut-api/import-shortcut-api.service";

import { AuthInterceptor } from "./interceptors/auth.interceptor";

import { CommonPipe } from "./filter/common.pipe";

import { ValidationConstants } from "./constants/validation.constants";
import { ApplicationConstants } from "./constants/application.constants";

import { ImportDialogComponent } from "./import-dialog/import-dialog.component";
import { ImportPreviewDialogComponent } from "./import-preview-dialog/import-preview-dialog.component";
import { LoadingDialogComponent } from "./loading-dialog/loading-dialog.component";
import { ErrorDialogComponent } from "./services/dataservices/error-dialog/error-dialog.component";
import { StripeService } from "./services/stripe.service";
import { TemplateService } from "./services/template.service";
import { WarningDialogComponent } from "./warning-dialog/warning-dialog.component";
import { PinToBrowserComponent } from "src/app/dashboard/pin-to-browser/pin-to-browser.component";
import { GiphyDialogComponent } from "./giphy-dialog/giphy-dialog.component";
import { GridGalleryItemComponent } from "./giphy-dialog/grid-gallery-item/grid-gallery-item.component";
import { CheckItemCompletedComponent } from "src/app/dashboard/walk-me/check-item-completed/check-item-completed.component";
import { SvgIconsModule } from "src/app/module/svg.module";
import { DialogCloseButtonComponent } from "src/app/components/dialogs/dialog-close-button/dialog-close-button.component";
import {
  VngDialogCloseDirective,
  VngDialogTitleDirective,
  VngDialogActionsComponent,
  VngButtonComponent,
  VngIconComponent,
} from "@vengreso/angular-components";

// Restore shared data from localstorage before app loads
export function restoreSharedData(appLoadService: AppLoadService) {
  return () => appLoadService.restoreSharedData();
}

@NgModule({
  declarations: [
    CommonPipe,
    ImportDialogComponent,
    ImportPreviewDialogComponent,
    LoadingDialogComponent,
    ErrorDialogComponent,
    WarningDialogComponent,
    GiphyDialogComponent,
    GridGalleryItemComponent,
    PinToBrowserComponent,
    CheckItemCompletedComponent,
  ],
  providers: [
    AppLoadService,
    {
      provide: APP_INITIALIZER,
      useFactory: restoreSharedData,
      deps: [AppLoadService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    AuthService,
    TemplateService,
    AuthGuardService,
    NotificationService,
    SharedDataService,
    NavigationService,
    ShortcutService,
    ExtensionService,
    DialogService,
    DataService,
    ScrollService,
    EventService,
    ListService,
    ImportShortcutApiService,
    StripeService,
    ApplicationConstants,
    ValidationConstants,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SvgIconsModule,
    InfiniteScrollModule,
    // vng (@vengreso/angular-components) — ADDED for C3-1. ErrorDialogComponent's
    // close button uses [vngDialogClose] (VngDialogRef has no MatDialogRef
    // DI fallback, so the old [mat-dialog-close] would throw on click).
    VngDialogCloseDirective,
    // ADDED for C3-13 (shared core dialogs sweep) — confirm-dialog/warning-dialog/
    // giphy-dialog/import-dialog/error-dialog templates now use vng-button/vng-icon/
    // [vng-dialog-title]/<vng-dialog-actions>.
    VngDialogTitleDirective,
    VngDialogActionsComponent,
    VngButtonComponent,
    VngIconComponent,
    // Shared corner "X" dismiss button — used by WarningDialogComponent
    // (declared here).
    DialogCloseButtonComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    SvgIconsModule,
    NgbPopoverModule,
    PinToBrowserComponent,
    CheckItemCompletedComponent,
  ],
})
export class CoreModule {}
